import { useState } from "react";
import { toast } from "sonner";
import { HeartPulse, ShieldCheck, LogOut, Loader2, FileText } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shown once per account (at registration). Returning users whose terms_accepted
// is already true pass straight through.
export default function TermsGate({ children }) {
  const { user, logout, refresh } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user?.terms_accepted) return children;

  const accept = async () => {
    if (!agreed) { toast.error("Please tick the box to continue"); return; }
    setBusy(true);
    try {
      await api.post("/terms/accept");
      await refresh();
      toast.success("Welcome to MDvia Care!");
    } catch { toast.error("Could not save your acceptance. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBFBF7] p-4" data-testid="terms-gate">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-amber-100/70 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#1E5AA8] flex items-center justify-center"><HeartPulse className="w-5 h-5 text-amber-300" /></div>
          <div>
            <h1 className="font-display text-lg font-bold text-[#123C73] leading-none">MDvia Care</h1>
            <p className="text-[11px] text-slate-500 mt-0.5">One quick step before you start</p>
          </div>
        </div>
        <div className="px-6 py-6">
          <div className="flex items-center gap-2 text-[#1E5AA8] mb-3"><ShieldCheck className="w-5 h-5" /><h2 className="font-semibold text-slate-900">Accept our Terms to continue</h2></div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Please review and accept our policies to create your MDvia Care account. You only need to do this once.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {[["Terms & Conditions", "/terms"], ["Privacy Policy", "/privacy"], ["Refund Policy", "/refund-policy"], ["Medical Disclaimer", "/disclaimer"]].map(([t, href]) => (
              <a key={href} data-testid={`terms-link-${href.replace(/\//g, "")}`} href={href} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E5AA8] hover:underline"><FileText className="w-4 h-4" />{t}</a>
            ))}
          </div>
          <label className="mt-6 flex items-start gap-2.5 cursor-pointer">
            <input data-testid="terms-gate-checkbox" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#1E5AA8] shrink-0" />
            <span className="text-sm text-slate-600 leading-relaxed">I have read and agree to the Terms &amp; Conditions, Privacy Policy, Refund Policy and Medical Disclaimer.</span>
          </label>
          <button data-testid="terms-gate-accept" onClick={accept} disabled={busy || !agreed}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1E5AA8] hover:bg-[#123C73] text-white font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Accept & continue
          </button>
          <button data-testid="terms-gate-logout" onClick={logout} className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-red-600 py-2">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
